import { Component, Inject, OnDestroy, OnInit, Optional, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ControlContainer, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { showAnimation } from 'src/animations';
import { Issue, NotificationTask, NotificationTaskType } from 'src/app/classes';
import { IIssueProviderService, IIssueTransactionProviderService, IIssueTransactionView, ISSUE_PROVIDER_SERVICE, ISSUE_TRANSACTION_PROVIDER_SERVICE } from '../../../interfaces';
import { NotificationPlaceholderReplacementService } from '../../../services/notification-placeholder-replacement.service';
import { ISSUE_FORM_GROUP, Step, STEP_FORM_GROUP } from '../../dialog/issue-transaction-dialog2/issue-transaction-dialog2-component-classes';

@Component({
  selector: 'app-notification-builder',
  templateUrl: './notification-builder.component.html',
  styleUrls: ['./notification-builder.component.css'],
  animations: [showAnimation]
})
export class NotificationBuilderComponent implements IIssueTransactionView, OnDestroy, OnInit {

  @ViewChild("notificationContentOutlet", { read: ViewContainerRef }) outletRef: ViewContainerRef;
  @ViewChild("notificationContent", { read: TemplateRef }) contentRef: TemplateRef<any>;
  @ViewChild("noNotificationSelected", { read: TemplateRef }) noContentRef: TemplateRef<any>;

  formGroup: FormGroup;

  selectedIndex: number = 0;

  toggled: boolean = false;

  issueTemplate: Issue = null;

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(ISSUE_PROVIDER_SERVICE) private _issueProviderService: IIssueProviderService,
    @Inject(ISSUE_TRANSACTION_PROVIDER_SERVICE) private _issueTransactionProviderService: IIssueTransactionProviderService,
    @Optional() @Inject('step') private _step: Step,
    @Optional() @Inject(STEP_FORM_GROUP) private _formGroup: FormGroup,
    @Optional() @Inject(ISSUE_FORM_GROUP) public issueFormGroup: FormGroup,
    @Optional() private _controlContainer: ControlContainer,
    private _notificationPlaceholderReplacementService: NotificationPlaceholderReplacementService
  ) {
    this.formGroup = this._formGroup;
  }

  addEMail() {
    this.notificationsArray.push(new FormGroup({
      subject: new FormControl(null, Validators.required),
      receiver: new FormControl([], Validators.compose([Validators.required, Validators.email])),
      content: new FormControl(null),
      type: new FormControl(NotificationTaskType.EMail),
      isDefaultNotification: new FormControl(null),
      attachements: new FormArray([])
    }));
    this.setNotificationIndex(this.notificationsArray.controls.length - 1);
  }

  addInternalMessage() {
    this.notificationsArray.push(new FormGroup({
      subject: new FormControl(null, Validators.required),
      receiver: new FormControl(null, Validators.required),
      content: new FormControl(null),
      type: new FormControl(NotificationTaskType.Internal),
      isDefaultNotification: new FormControl(null),
      attachements: new FormArray([])
    }));
    this.setNotificationIndex(this.notificationsArray.controls.length - 1);
  }

  deleteNotificationAtIndex(index: number) {
    this.notificationsArray.removeAt(index);
    this.setNotificationIndex(0);
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(x => x.unsubscribe());
    this._subscriptions = [];
  }

  ngOnInit(): void {

    if (!this.formGroup && this._controlContainer.control) this.formGroup = this._controlContainer.control as FormGroup;

    this._subscriptions.push(...[
      this.notificationsArray.valueChanges.subscribe((value: NotificationTask[]) => {
        if (this._step) this._step.visibleInConclusion = value.length > 0 ? true : false;
      })
    ]);

    this.validateContentView();
    if (this.issueFormGroup) this.issueTemplate = this.issueFormGroup.value;
    this.overwriteDefaultNotificationPlaceholders();

  }

  setNotificationIndex(index: number) {
    this.selectedIndex = index;
    this.validateContentView();
  }

  overwriteDefaultNotificationPlaceholders() {
    for (let notification of this.notificationsArray.controls) {

      if (
        (notification.value as NotificationTask).isDefaultNotification
        && (notification.value as NotificationTask).defaultNotification
        && !(notification.value as NotificationTask).content
      ) {
        (notification as FormGroup).controls['subject'].setValue((notification.value as NotificationTask).defaultNotification.subject);
        (notification as FormGroup).controls['content'].setValue((notification.value as NotificationTask).defaultNotification.htmlContent);
        combineLatest([this._issueProviderService.issue$, this._issueTransactionProviderService.issueTransaction$]).pipe(take(1), switchMap(([issue, transaction]) => this._notificationPlaceholderReplacementService.replace(notification.value, issue, transaction)))
          .subscribe((result: NotificationTask) => {
            (notification as FormGroup).controls['content'].setValue(result.content);
            (notification as FormGroup).controls['subject'].setValue(result.subject);
          });
      }
    }
  }

  validateContentView() {
    if (this.outletRef) {
      this.outletRef.clear();
      this.outletRef.createEmbeddedView(this.notificationsArray.controls[this.selectedIndex] ? this.contentRef : this.noContentRef);
    }
  }

  get notificationsArray(): FormArray {
    return this.formGroup.controls['notifications'] as FormArray;
  }

}
